import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ApplicationResultReferralGate from "src/features/application-result-referral/components/ApplicationResultReferralGate";
import { trackEvent } from "src/hooks/useInstrumentation";
import { useCurrentUser } from "src/SessionBoundary";
import ConfirmDialog from "src/shared/components/ConfirmDialog";
import FullscreenBox from "src/shared/components/FullScreenBox";
import { ScreenTitle } from "src/shared/components/Typography";
import ReactivationDialog from "./ReactivationDialog";
import resolveRejectedApplicationDetails from "./resolveRejectedApplicationDetails";

const DisabledReasonBody = () => {
  const user = useCurrentUser();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const rejectionDetails = resolveRejectedApplicationDetails(user);

  const handleEditApplicationClick = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmEdit = () => {
    setShowConfirmDialog(false);
    trackEvent("Rejected Application Screen - CLicked on Edit Application", {
      userId: user.uid,
    });
    navigate("/onboarding");
  };

  const handleCancelEdit = () => {
    setShowConfirmDialog(false);
  };

  switch (user.applicationStatus) {
    case "rejected":
      return (
        <>
          <Card sx={{ width: "100%", maxWidth: 600 }}>
            <CardContent>
              <Stack spacing={2}>
                <Typography>
                  {t(
                    "{{ firstName }}, our team has finished reviewing the application form you submitted.",
                    {
                      firstName: user.firstName,
                    }
                  )}
                </Typography>
                <Typography>
                  {rejectionDetails
                    ? t(
                        "Unfortunately, we cannot move you forward in the process for the following reason:"
                      )
                    : t(
                        "Unfortunately, based on the information you provided, we cannot move you forward in the application process at this time."
                      )}
                </Typography>

                <Box sx={{ paddingX: 2 }}>
                  <Typography fontWeight="bold" sx={{ whiteSpace: "pre-line" }}>
                    {rejectionDetails}
                  </Typography>
                </Box>
                <Typography marginTop={2}>
                  {t(
                    "We know mistakes can happen when filling out the application. Do we have the wrong information?"
                  )}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleEditApplicationClick}
                  sx={{ alignSelf: "flex-start" }}
                >
                  {t("Update Application")}
                </Button>
              </Stack>
            </CardContent>
          </Card>
          <ConfirmDialog
            open={showConfirmDialog}
            title={t("Update Application")}
            description={t(
              "We trust you! Your application information will be reviewed and verified when we reach the enrollment stage."
            )}
            onConfirm={handleConfirmEdit}
            onCancel={handleCancelEdit}
            buttonColor="primary"
          />
        </>
      );

    case "withdrawn":
    case "inactive":
    case "dropped":
    default:
      return <ReactivationDialog />;
  }
};

export default function DisabledAccountScreen() {
  const { t } = useTranslation();
  const user = useCurrentUser();

  useEffect(() => {
    trackEvent("Disabled Account Screen - Viewed", {
      applicationStatus: user.applicationStatus,
    });
  }, [user.applicationStatus]);

  const screen = (
    <FullscreenBox
      sx={(theme) => ({
        backgroundColor: theme.palette.background.paper,
      })}
    >
      <Stack
        spacing={6}
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          paddingX: theme.spacing(2),
          marginTop: "100px",
        })}
      >
        <ScreenTitle sx={{ textAlign: "center" }}>
          {t("Welcome back, {{ firstName }}!", { firstName: user.firstName })}
        </ScreenTitle>
        <DisabledReasonBody />
      </Stack>
    </FullscreenBox>
  );

  // Rejected students in the `application-result-referral-ask` test arm see
  // the referral ask once before this screen.
  if (user.applicationStatus !== "rejected") return screen;
  return (
    <ApplicationResultReferralGate surface="rejected" active>
      {screen}
    </ApplicationResultReferralGate>
  );
}
